import { QueryClient } from '@tanstack/react-query';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { del, get, set } from 'idb-keyval';

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            gcTime: 24 * 60 * 60_000,
            retry: 1,
            refetchOnWindowFocus: false,
        },
        mutations: {
            retry: 0,
        },
    },
});

export const QUERY_CACHE_KEY = 'capital-r.query-cache';

/**
 * Versão do formato dos payloads em cache. O cache persiste por 24h, então
 * mudança no shape de uma resposta faz o app renderizar dado antigo com código
 * novo (e quebrar no primeiro campo que não existia).
 *
 * **Suba esta versão sempre que remover ou renomear campo de payload cacheado.**
 */
export const QUERY_CACHE_VERSION = '2026-10-27-cpf-financeiro';

/**
 * O cache vive no IndexedDB, não no localStorage: a gravação é assíncrona (nada
 * de serializar MBs na thread principal a cada segundo) e não existe a cota de
 * 5 MB que estourava em silêncio com os payloads do BI. É esse cache que faz um
 * painel já visitado pintar antes de a rede responder.
 */
const hasIndexedDb = typeof window !== 'undefined' && typeof indexedDB !== 'undefined';

export const queryPersister = createAsyncStoragePersister({
    storage: hasIndexedDb
        ? {
              getItem: (key) => get<string>(key).then((value) => value ?? null),
              setItem: (key, value) => set(key, value),
              removeItem: (key) => del(key),
          }
        : undefined,
    key: QUERY_CACHE_KEY,
    throttleTime: 1_000,
});

export const clearPersistedQueryCache = async (): Promise<void> => {
    try {
        await queryPersister.removeClient();
    } catch {
        /* noop */
    }
};

// O cache antigo morava no localStorage sob a mesma chave; libera os ~5 MB.
if (typeof window !== 'undefined') {
    try {
        window.localStorage.removeItem(QUERY_CACHE_KEY);
    } catch {
        /* noop */
    }
}
